import { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Card, Form, Table, Spinner, Button, Badge, ProgressBar } from "react-bootstrap";
import { BiRefresh, BiData, BiTime, BiFileBlank, BiTag, BiCheckCircle, BiXCircle } from "react-icons/bi";
import { getDataDashboard } from "../api/getapi";
import { formatNumber } from "../Utilities/numbers";
import { ECHART_COLORS } from "../Utilities/echartsTheme";
import StatCard from "../Components/DashboardCards/StatCard/StatCard";
import EChartsDoughnut from "../Components/Charts/EChartsDoughnut";
import EChartsHorizontalBar from "../Components/Charts/EChartsHorizontalBar";
import EChartsVerticalBar from "../Components/Charts/EChartsVerticalBar";
import moment from "moment";

const spinKeyframes = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 0.8s linear infinite; display: inline-block; }`;

const timePeriods = [
  { value: "all-time", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this-week", label: "This Week" },
  { value: "this-month", label: "This Month" },
  { value: "this-year", label: "This Year" },
  { value: "last-year", label: "Last Year" },
];

const statusBadgeVariant = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "live") return "success";
  if (s === "draft") return "warning";
  if (s === "under review") return "info";
  return "secondary";
};

const DataDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState("all-time");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDataDashboard({ timePeriod });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [timePeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData = useMemo(() => {
    if (!data?.modelsOverTime) return null;
    return data.modelsOverTime;
  }, [data]);

  const categoryChartData = useMemo(() => {
    if (!data?.modelsByCategory) return {};
    const chartData = {};
    data.modelsByCategory.slice(0, 8).forEach((c) => {
      chartData[c.category_name] = Number(c.count);
    });
    return chartData;
  }, [data]);

  const metaBarData = useMemo(() => {
    if (!data?.metaStats) return [];
    return [
      { label: "Has Title", value: Number(data.metaStats.hasTitle || 0) },
      { label: "Has Description", value: Number(data.metaStats.hasDescription || 0) },
      { label: "Has Keywords", value: Number(data.metaStats.hasKeywords || 0) },
    ];
  }, [data]);

  const totalModels = data?.summary?.totalModels || 0;
  const metaCoverage = totalModels
    ? Math.round((Number(data?.metaStats?.modelsWithMeta || 0) / totalModels) * 100)
    : 0;

  return (
    <Container fluid className="px-xl-5">
      <style>{spinKeyframes}</style>
      <Row className="py-2 align-items-center">
        <Col md="6">
          <h5 className="fw-bold">Data Dashboard</h5>
          <p className="text-muted mb-0">Models, categories, and meta information stats.</p>
        </Col>
        <Col md="6" className="text-md-end mt-3 mt-md-0">
          <Button
            variant="light"
            size="sm"
            className="me-2 border"
            onClick={fetchData}
            disabled={loading}
          >
            <BiRefresh size={20} className={loading ? "spin" : ""} />
          </Button>
          <Form.Select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            style={{ width: 180, display: "inline-block" }}
          >
            {timePeriods.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {loading && (
        <div className="text-center mt-4">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPI Stat Cards */}
          <Row className="g-3 mt-1 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Total Models"
                count={formatNumber(data.summary?.totalModels || 0)}
                icon={<BiData />}
                color={ECHART_COLORS.primary}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Under Review"
                count={formatNumber(data.summary?.underReview || 0)}
                icon={<BiTime />}
                color={ECHART_COLORS.accentLight}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="In Draft"
                count={formatNumber(data.summary?.inDraft || 0)}
                icon={<BiFileBlank />}
                color={ECHART_COLORS.purple}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Models with Meta"
                count={formatNumber(data.metaStats?.modelsWithMeta || 0)}
                icon={<BiTag />}
                color={ECHART_COLORS.success}
              />
            </Col>
          </Row>

          {/* Charts Row */}
          <Row className="g-3 mb-4">
            <Col md="12" lg="8" sm="12" xl="8">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Models Over Time</h6>
                {chartData && (
                  <div style={{ height: 300 }}>
                    <EChartsVerticalBar data={chartData} label="Models" />
                  </div>
                )}
              </Card>
            </Col>
            <Col md="12" lg="4" sm="12" xl="4">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Category Distribution</h6>
                <div style={{ height: 300 }}>
                  <EChartsDoughnut data={categoryChartData} />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Recent Models & Meta Stats */}
          <Row className="g-3 mb-4">
            <Col md="12" lg="7" sm="12" xl="7">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">10 Recent Models</h6>
                <div className="table-responsive">
                  <Table hover size="sm" className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-center" style={{ width: 40 }}>#</th>
                        <th>Title</th>
                        <th>Brand</th>
                        <th>Category</th>
                        <th>Status</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.recentModels || []).map((row, i) => (
                        <tr key={row.model_id}>
                          <td className="text-center text-muted">{i + 1}</td>
                          <td>
                            <div className="fw-medium">{row.model_title}</div>
                            <small className="text-muted">{row.model_name}</small>
                          </td>
                          <td>{row.brand_name}</td>
                          <td>{row.category_name}</td>
                          <td>
                            <Badge bg={statusBadgeVariant(row.status)} pill>
                              {row.status || "—"}
                            </Badge>
                          </td>
                          <td>{moment(row.created_at).format("DD MMM YYYY")}</td>
                        </tr>
                      ))}
                      {(!data.recentModels || data.recentModels.length === 0) && (
                        <tr>
                          <td colSpan="6" className="text-center text-muted py-3">
                            No recent models found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
            <Col md="12" lg="5" sm="12" xl="5">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Meta Information Coverage</h6>

                {/* Meta coverage progress */}
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>Meta Coverage</span>
                    <span className="fw-bold">{metaCoverage}%</span>
                  </div>
                  <ProgressBar
                    now={metaCoverage}
                    variant={metaCoverage >= 80 ? "success" : metaCoverage >= 50 ? "warning" : "danger"}
                    style={{ height: 8 }}
                  />
                </div>

                {/* Meta breakdown bar chart */}
                <div style={{ height: 180 }}>
                  <EChartsHorizontalBar
                    data={metaBarData}
                    label="Models"
                    multiColor
                  />
                </div>

                {/* Meta summary stats */}
                <div className="d-flex flex-wrap gap-3 mt-3 pt-2 border-top">
                  <div className="d-flex align-items-center gap-1">
                    <BiCheckCircle className="text-success" />
                    <small className="text-muted">With Meta:</small>
                    <strong>{formatNumber(data.metaStats?.modelsWithMeta || 0)}</strong>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <BiXCircle className="text-danger" />
                    <small className="text-muted">Without Meta:</small>
                    <strong>{formatNumber(data.metaStats?.modelsWithoutMeta || 0)}</strong>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <BiTag className="text-primary" />
                    <small className="text-muted">Total Records:</small>
                    <strong>{formatNumber(data.metaStats?.totalMeta || 0)}</strong>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Models by Category Table */}
          <Row className="g-3 mb-4">
            <Col md="12">
              <Card className="border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Models by Category</h6>
                <div className="table-responsive">
                  <Table hover size="sm" className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-center" style={{ width: 40 }}>#</th>
                        <th>Category</th>
                        <th className="text-end" style={{ width: 100 }}>Models</th>
                        <th style={{ width: 200 }} title="Models relative to the top category">
                          % of Top
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.modelsByCategory || []).map((row, i) => {
                        const maxCount = data.modelsByCategory?.[0]?.count || 1;
                        const pct = Math.round((Number(row.count) / maxCount) * 100);
                        return (
                          <tr key={row.category_name}>
                            <td className="text-center text-muted">{i + 1}</td>
                            <td className="fw-medium">{row.category_name}</td>
                            <td className="text-end fw-bold">{formatNumber(row.count)}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <ProgressBar
                                  now={pct}
                                  variant={pct >= 75 ? "success" : pct >= 40 ? "primary" : "info"}
                                  style={{ height: 6, flex: 1 }}
                                />
                                <small className="text-muted" style={{ minWidth: 36 }}>{pct}%</small>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {(!data.modelsByCategory || data.modelsByCategory.length === 0) && (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-3">
                            No category data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default DataDashboard;
